import { Component, Input, Output, EventEmitter, ViewChild, signal, computed, effect, untracked, ElementRef, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { MatIconModule } from '@angular/material/icon';
import { ConfirmPopup } from '../confirm-popup/confirm-popup';
import { CopyableTruncateTooltipDirective } from '@app/shared/directives/copyable-truncate-tooltip.directive';
import { trigger, style, transition, animate } from '@angular/animations';

export interface AssigneeUser {
  _id: string;
  name: string;
  image?: string;
  assignType?: string;
  email?: string;
  jobrole?: string;
  teamName?: string;
  avatar?: string;
  [key: string]: any;
}

export interface AssigneeSelectionConfig {
  title?: string;
  placeholder?: string;
  confirmText?: string;
  cancelText?: string;
  loadingText?: string;
  noResultsText?: string;
  noChangesMessage?: string;
}

@Component({
  selector: 'app-assignee-selection-expand',
  standalone: true,
  imports: [
    CommonModule,
    NgSelectModule,
    FormsModule,
    MatIconModule,
    ConfirmPopup,
    CopyableTruncateTooltipDirective
  ],
  templateUrl: './assignee-selection-expand.html',
  styleUrl: './assignee-selection-expand.scss',
  animations: [
    trigger('expandCollapse', [
      transition(':enter', [
        style({ 
          height: '0', 
          opacity: 0,
          transform: 'translateY(-8px)',
          overflow: 'hidden'
        }),
        animate('350ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ 
          height: '*', 
          opacity: 1,
          transform: 'translateY(0)',
          overflow: 'hidden'
        }))
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate('250ms cubic-bezier(0.4, 0.0, 1, 1)', style({ 
          height: '0', 
          opacity: 0,
          transform: 'translateY(-8px)',
          overflow: 'hidden'
        }))
      ])
    ])
  ]
})
export class AssigneeSelectionExpand {
  // Input signals using Angular 20 input() function
  @Input() set expanded(value: boolean) {
    this._expanded.set(value);
  }
  @Input() set allAssignableData(value: AssigneeUser[]) {
    this._allAssignableData.set(value);
  }
  @Input() set assignedUsers(value: AssigneeUser[]) {
    this._assignedUsers.set(value);
  }
  @Input() set isLoadingData(value: boolean) {
    this._isLoadingData.set(value);
  }
  @Input() set config(value: AssigneeSelectionConfig) {
    this._config.set(value);
  }

  // Output events
  @Output() assignUsers = new EventEmitter<{
    newlySelectedUsers: AssigneeUser[];
    removedUsers: AssigneeUser[];
  }>();
  @Output() collapse = new EventEmitter<void>();

  // ViewChild
  @ViewChild('assigneeSelect') assigneeSelect!: NgSelectComponent;
  @ViewChild('assignSelectWrap') assignSelectWrap?: ElementRef<HTMLElement>;
  private readonly destroyRef = inject(DestroyRef);

  // Private signals
  private _expanded = signal(false);
  private _allAssignableData = signal<AssigneeUser[]>([]);
  private _assignedUsers = signal<AssigneeUser[]>([]);
  private _isLoadingData = signal(false);
  private _config = signal<AssigneeSelectionConfig>({});

  // Public signals
  selectedUsers = signal<AssigneeUser[]>([]);
  selectedUserIds = signal<string[]>([]);
  assigneeSearch = signal('');
  filteredAssignees = signal<AssigneeUser[]>([]);
  showDropdown = signal(false);
  showNoChangesMessage = signal(false);
  showAssignConfirmPopup = signal(false);
  isAssigningUsers = signal(false);
  previouslyAssignedUsers = signal<AssigneeUser[]>([]);
  imageLoadError: { [key: string]: boolean } = {};

  // Computed signals
  ngSelectItems = computed(() => {
    const uniqueUsersMap = new Map<string, AssigneeUser>();
    const selectedIds = new Set(this.selectedUserIds());
    const search = this.assigneeSearch().trim().toLowerCase();

    this._allAssignableData().forEach(user => {
      uniqueUsersMap.set(user._id, user);
    });

    this._assignedUsers().forEach(user => {
      if (!uniqueUsersMap.has(user._id)) {
        uniqueUsersMap.set(user._id, user);
      }
    });

    return Array.from(uniqueUsersMap.values()).filter(user => {
      // Keep selected users in items so ng-select does not drop them from the model.
      // hideSelected hides them from the dropdown list.
      if (selectedIds.has(user._id)) {
        return true;
      }
      if (!search) {
        return true;
      }
      return this.getDisplayName(user).toLowerCase().includes(search);
    });
  });

  // Computed config with defaults
  expandConfig = computed(() => {
    const config = this._config();
    return {
      title: config.title || 'Assign',
      placeholder: config.placeholder || 'Add assignee',
      confirmText: config.confirmText || 'Assign',
      cancelText: config.cancelText || 'Cancel',
      loadingText: config.loadingText || 'Loading...',
      noResultsText: config.noResultsText || 'No assignees found',
      noChangesMessage: config.noChangesMessage || 'No changes made to assignees'
    };
  });

  constructor() {
    let wasOpen = false;

    effect(() => {
      const isOpen = this._expanded();
      if (!isOpen) {
        this.showAssignConfirmPopup.set(false);
        this.isAssigningUsers.set(false);
        wasOpen = false;
        return;
      }
      if (!wasOpen) {
        untracked(() => {
          this.loadSelectedUsers();
          this.resetState();
        });
        wasOpen = true;
      }
    });

    const onDocumentMouseDown = (event: MouseEvent) => this.closeAssigneeDropdownOnOutsideClick(event);
    const onDocumentScroll = (event: Event) => this.closeAssigneeDropdownOnPageScroll(event);
    document.addEventListener('mousedown', onDocumentMouseDown, true);
    document.addEventListener('scroll', onDocumentScroll, true);
    window.addEventListener('scroll', onDocumentScroll, true);
    this.destroyRef.onDestroy(() => {
      document.removeEventListener('mousedown', onDocumentMouseDown, true);
      document.removeEventListener('scroll', onDocumentScroll, true);
      window.removeEventListener('scroll', onDocumentScroll, true);
    });
  }

  collapsePanel = () => {
    if (this.isAssigningUsers()) {
      return;
    }
    this._expanded.set(false);
    this.selectedUsers.set([]);
    this.selectedUserIds.set([]);
    this.assigneeSearch.set('');
    this.filteredAssignees.set([]);
    this.showDropdown.set(false);
    this.clearAssigneeSearch();
    this.collapse.emit();
  };

  // User selection methods
  onUserSelectionChange = (userIds: string[]) => {
    const currentSelectedSet = new Set(this.selectedUserIds());
    const newIds = userIds.filter(id => !currentSelectedSet.has(id));
    const dedupedUserIds = [...new Set(userIds)];
    const existingIdsInSelection = this.selectedUserIds().filter((id) => dedupedUserIds.includes(id));
    // Keep newly added assignees at top so assignment feedback is immediate.
    const finalUserIds = [...newIds, ...existingIdsInSelection];
    
    this.selectedUserIds.set(finalUserIds);
    
    const uniqueUsersMap = new Map<string, AssigneeUser>();
    
    this._assignedUsers().forEach(user => {
      const displayName = user.name || user.jobrole || user.teamName || 'Unknown';
      uniqueUsersMap.set(user._id, {
        ...user,
        _id: user._id,
        name: displayName,
        image: user.image || user.avatar || user.image
      });
    });
    
    this._allAssignableData().forEach(user => {
      if (!uniqueUsersMap.has(user._id)) {
        const displayName = user.name || user.jobrole || user.teamName || 'Unknown';
        uniqueUsersMap.set(user._id, {
          ...user,
          _id: user._id,
          name: displayName,
          image: user.image || user.avatar || user.image
        });
      }
    });
    
    this.selectedUsers.set(
      finalUserIds
        .map(id => uniqueUsersMap.get(id))
        .filter(user => user !== undefined)
        .map(user => {
          const displayName = user!.name || user!.jobrole || user!.teamName || 'Unknown';
          return {
            _id: user!._id,
            name: displayName,
            image: user!.image || user!.avatar,
            assignType: user!.assignType 
          };
        })
    );
    
    this.checkForChanges();
  };

  removeSelectedUser = (index: number) => {
    const arr = [...this.selectedUsers()];
    const removedUser = arr.splice(index, 1)[0];
    this.selectedUsers.set(this.ensureUniqueUsers(arr));
    this.selectedUserIds.set(this.selectedUserIds().filter(id => id !== removedUser._id));
    this.checkForChanges();
  };

  // Search and filter methods
  filterAssignees = () => {
    const search = this.assigneeSearch().toLowerCase();
    const selectedUserIdsSet = new Set(this.selectedUserIds());
    
    const allUsersMap = new Map<string, AssigneeUser>();
    this._allAssignableData().forEach(user => {
      allUsersMap.set(user._id, user);
    });
    const currentData = Array.from(allUsersMap.values());
    
    this.filteredAssignees.set(
      currentData.filter(item => {
        if (selectedUserIdsSet.has(item._id)) {
          return false;
        }
        const displayName = this.getDisplayName(item);
        return displayName.toLowerCase().includes(search);
      })
    );
  };

  onAssigneeSearch = (event: any) => {
    this.assigneeSearch.set(event.term || '');
  };

  // NgSelect event handlers
  onAssigneeAdd = () => this.clearSearchInputQuietly();
  onAssigneeRemove = () => {};
  onAssigneeSelectionChanges = () => this.clearSearchInputQuietly();
  onAssigneeSelect = () => this.clearSearchInputQuietly();
  onAssigneeChange = () => this.clearSearchInputQuietly();
  onAssigneeBlur = (event?: FocusEvent) => {
    if (this.isCopyableTooltipInteraction(event?.relatedTarget)) {
      return;
    }
    this.clearSearchInputQuietly();
  };
  onAssigneeOpen = () => {
    this.showDropdown.set(true);
    this.scheduleAssigneeDropdownPanelTag();
  };

  onAssigneeDropdownClose(): void {
    if (this.isCopyableTooltipInteraction()) {
      queueMicrotask(() => {
        this.showDropdown.set(true);
        this.assigneeSelect?.open();
      });
      return;
    }
    this.showDropdown.set(false);
  }

  private closeAssigneeDropdownOnOutsideClick(event: MouseEvent): void {
    if (!this.showDropdown() && !this.assigneeSelect?.isOpen) {
      return;
    }
    if (this.isAssigneeSelectSurface(event.target)) {
      return;
    }
    this.closeAssigneeDropdown();
  }

  /** Close when the page/sidebar scrolls; keep open while scrolling inside the panel. */
  private closeAssigneeDropdownOnPageScroll(event: Event): void {
    if (!this.showDropdown() && !this.assigneeSelect?.isOpen) {
      return;
    }
    const target = event.target;
    if (target instanceof Element || target instanceof Node) {
      const el = target instanceof Element ? target : target.parentElement;
      if (el?.closest('.ng-dropdown-panel, .assignee-expand-dropdown-panel')) {
        return;
      }
      // Ignore scroll inside the select input wrap itself (e.g. multi-value chips).
      if (el && this.assignSelectWrap?.nativeElement.contains(el)) {
        return;
      }
    }
    this.closeAssigneeDropdown();
  }

  private closeAssigneeDropdown(): void {
    this.showDropdown.set(false);
    this.assigneeSelect?.close();
  }

  /** Select wrap, its body-appended list, or the copyable name tooltip. */
  private isAssigneeSelectSurface(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) {
      return false;
    }
    if (this.assignSelectWrap?.nativeElement.contains(target)) {
      return true;
    }
    // Body-appended panel: class may be added a tick late, so accept any open panel.
    if (target.closest('.assignee-expand-dropdown-panel, .ng-dropdown-panel')) {
      return true;
    }
    if (target.closest('.app-copyable-truncate-tooltip-panel')) {
      return true;
    }
    return false;
  }

  private isCopyableTooltipInteraction(relatedTarget?: EventTarget | null): boolean {
    if (relatedTarget instanceof Element && relatedTarget.closest('.app-copyable-truncate-tooltip-panel')) {
      return true;
    }
    const panel = document.querySelector('.app-copyable-truncate-tooltip-panel');
    return !!panel?.matches(':hover');
  }

  private scheduleAssigneeDropdownPanelTag(): void {
    requestAnimationFrame(() => this.tagAssigneeDropdownPanel());
    window.setTimeout(() => this.tagAssigneeDropdownPanel(), 0);
    window.setTimeout(() => this.tagAssigneeDropdownPanel(), 50);
  }

  /** Body-appended panel is outside encapsulation; tag it so panel chrome can be scoped. */
  private tagAssigneeDropdownPanel(): void {
    const host = this.assigneeSelect?.element as HTMLElement | undefined;
    const panels = Array.from(document.querySelectorAll<HTMLElement>('.ng-dropdown-panel'));
    if (!panels.length) {
      return;
    }

    let panel = panels[panels.length - 1];
    if (host) {
      const hostRect = host.getBoundingClientRect();
      let bestDistance = Infinity;
      for (const candidate of panels) {
        if (candidate.querySelector('.assignee-expand-option')) {
          panel = candidate;
          break;
        }
        const rect = candidate.getBoundingClientRect();
        const distance =
          Math.abs(rect.top - hostRect.bottom) + Math.abs(rect.left - hostRect.left);
        if (distance < bestDistance) {
          bestDistance = distance;
          panel = candidate;
        }
      }
    }
    panel.classList.add('assignee-expand-dropdown-panel');
  }

  // Search clearing methods
  private clearSearchInputQuietly = () => {
    this.assigneeSearch.set('');
    this.filteredAssignees.set([]);
    if (!this.assigneeSelect) {
      return;
    }
    this.assigneeSelect.searchTerm = '';
    const searchInput = this.assigneeSelect.searchInput?.nativeElement;
    if (searchInput) {
      searchInput.value = '';
    }
  };

  clearAssigneeSearch = () => {
    this.clearSearchInputQuietly();
  };

  // Assignment confirmation methods
  onAssignUsersClick = () => this.showAssignConfirmPopup.set(true);
  
  confirmAssignUsers = () => {
    if (this.isAssigningUsers()) {
      return;
    }
    this.isAssigningUsers.set(true);
    this._assignUsersWithApi();
  };

  cancelAssignUsers = () => {
    if (this.isAssigningUsers()) {
      return;
    }
    this.showAssignConfirmPopup.set(false);
  };
  private _assignUsersWithApi = () => {
    const newlySelectedUsers = this.selectedUsers().filter(selectedUser =>
      !this.previouslyAssignedUsers().some(prevUser => prevUser._id === selectedUser._id))
      .map(selectedUser => ({ ...selectedUser, assignType: selectedUser.assignType }));
    const removedUsers = this.previouslyAssignedUsers().filter(prevUser =>
      !this.selectedUsers().some(selectedUser => selectedUser._id === prevUser._id))
      .map(prevUser => ({ ...prevUser, assignType: prevUser.assignType }));

    if (newlySelectedUsers.length === 0 && removedUsers.length === 0) {
      this.showNoChangesMessage.set(true);
      this.isAssigningUsers.set(false);
      this.showAssignConfirmPopup.set(false);
      return;
    }

    this.assignUsers.emit({ newlySelectedUsers, removedUsers });
  };

  // Helper methods
  private ensureUniqueUsers(users: AssigneeUser[]): AssigneeUser[] {
    const uniqueUsersMap = new Map<string, AssigneeUser>();
    users.forEach(user => {
      uniqueUsersMap.set(user._id, user);
    });
    return Array.from(uniqueUsersMap.values());
  }

  private loadSelectedUsers = () => {
    this.previouslyAssignedUsers.set([...this._assignedUsers()]);
    this.selectedUsers.set([]);
    this.selectedUserIds.set([]);
    const uniqueUsersMap = new Map<string, AssigneeUser>();
    this._assignedUsers().forEach(user => {
      const displayName = user.name || user.jobrole || user.teamName || (user as any)['jobroles']?.jobrole || 'Unknown';
      uniqueUsersMap.set(user._id, {
        ...user,
        _id: user._id,
        name: displayName,
        image: user.image || user.avatar || user.image
      });
    });
    const arr = Array.from(uniqueUsersMap.values());
    this.selectedUsers.set(arr);
    this.selectedUserIds.set(arr.map(user => user._id));
  };

  private resetState = () => {
    this.showNoChangesMessage.set(false);
    this.showAssignConfirmPopup.set(false);
    this.isAssigningUsers.set(false);
    this.assigneeSearch.set('');
    this.filteredAssignees.set([]);
    this.showDropdown.set(false);
    this.imageLoadError = {};
  };

  private checkForChanges = () => {
    const newlySelectedUsers = this.selectedUsers().filter(selectedUser => 
      !this.previouslyAssignedUsers().some(prevUser => prevUser._id === selectedUser._id));
    const removedUsers = this.previouslyAssignedUsers().filter(prevUser => 
      !this.selectedUsers().some(selectedUser => selectedUser._id === prevUser._id));
    
    const hasChanges = newlySelectedUsers.length > 0 || removedUsers.length > 0;
    this.showNoChangesMessage.set(!hasChanges);
  };

  getDisplayName = (item: any): string => { 
    if (!item) return 'Unknown';
    return item.name || item.jobrole || item.teamName || item.jobroles?.jobrole || 'Unknown';
  };

  /** Native ng-select option titles steal hover; keep the copyable tooltip instead. */
  clearNativeOptionTitle(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }
    target.removeAttribute('title');
    target.closest('.ng-option')?.removeAttribute('title');
    target.closest('.ng-dropdown-panel')
      ?.querySelectorAll('.ng-option[title], .ng-option [title]')
      .forEach((el) => {
        if (el instanceof HTMLElement) {
          el.removeAttribute('title');
        }
      });
  }

  getInitials(name: string): string {
    if (!name || name.trim() === '') {
      return '?';
    }

    const words = name.trim().split(' ');
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }

    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  }

  getAvatarColor(userId: string): string {
    if (!userId) return '#6b7280';
    
    const colors = [
      '#8b5cf6', '#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#ec4899',
      '#14b8a6', '#f97316', '#6366f1', '#a855f7', '#06b6d4', '#84cc16',
    ];

    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      hash = userId.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  }

  hasValidImage(user: AssigneeUser): boolean {
    const imageUrl = user.image || user.avatar;
    return !!(imageUrl && !this.imageLoadError[user._id]);
  }

  onImageError(userId: string): void {
    this.imageLoadError[userId] = true;
  }

  // Getters for template access
  get expanded() {
    return this._expanded();
  }

  get allAssignableData() {
    return this._allAssignableData();
  }

  get assignedUsers() {
    return this._assignedUsers();
  }

  get isLoadingData() {
    return this._isLoadingData();
  }
}

